import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import _ from 'lodash';
import { forwardRef, useImperativeHandle, useState } from 'react';
import CircleActive from './CircleActive';
import { MobileSignalIcon } from './MobileSignalIcon';
import { red } from '@mui/material/colors';
import dayjs from 'dayjs';

export const paperWithLeftLabelAndRightValue = (label, value, padding = 1) => {
  return (
    <>
      {' '}
      <Paper
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: padding,
        }}
      >
        <Typography sx={{ width: '33%', flexShrink: 0 }}>{label}</Typography>
        <Typography sx={{ color: 'text.secondary' }}>{value}</Typography>
      </Paper>
    </>
  );
};

export const paperWithTopLabelAndBottomValue = ({
  label,
  value,
  width = '100%',
} = {}) => {
  return (
    <>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: 0.7,
          width: `${width}`,
          minHeight: '45.5px',
        }}
      >
        <Typography sx={{ flex: 1, fontSize: '12px' }}>{label}</Typography>
        <Typography sx={{ color: 'text.secondary' }}>{value}</Typography>
      </Paper>
    </>
  );
};

export const dateSelectorWithTopLabel = ({
  name,
  label,
  value,
  format = 'MM/dd/yyyy',
  width = '100%',
  onChange,
} = {}) => {
  let jsDate = new Date(value);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DesktopDatePicker
          fullWidth
          inputFormat={format}
          value={jsDate}
          disabled={true}
          onChange={value => {
            let event = {
              target: {},
            };
            event.target.name = name;
            event.target.value = value;
            onChange(event);
          }}
          renderInput={params => (
            <Box>
              <Typography sx={{ fontSize: 14 }}>{`${label}`} </Typography>
              <TextField
                sx={{ width: `${width}` }}
                {...params}
                size="small"
                disabled="disabled"
                readOnly={true}
              />
            </Box>
          )}
        />
      </LocalizationProvider>
    </>
  );
};

export const timepickerWithTopLabel = ({
  name,
  label,
  value,
  format = '',
  width = '100%',
  onChange,
} = {}) => {
  let jsTime = new Date(value);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TimePicker
        fullWidth
        name={name}
        value={jsTime}
        disabled={'disabled'}
        onChange={newValue => {
          let event = {
            target: {
              name: name,
              value: newValue,
            },
          };

          onChange(event);
        }}
        renderInput={params => (
          <Box>
            <Typography sx={{ fontSize: 14 }}>{`${label}`} </Typography>
            <TextField
              sx={{ width: `${width}` }}
              {...params}
              size="small"
              disabled={'disabled'}
            />
          </Box>
        )}
      />
    </LocalizationProvider>
  );
};

export const TextFieldWithTopLabelNoteComponent = forwardRef((props, ref) => {
  const {
    label,
    width = '100%',
    name,
    value,
    onChange,
    type = 'text',
    validation = {
      min: 1,
      max: 35,
      required: false,
    },
  } = props;

  const [errorText, setErrorText] = useState('');

  useImperativeHandle(ref, () => ({
    onValidate() {
      let isValid = false;

      if (value?.length > validation?.min && value?.length < validation?.max) {
        setErrorText('');
        isValid = true;
      } else {
        setErrorText(
          `${label} must be between ${validation?.min} and ${validation?.max}`
        );
        isValid = false;
      }
      return { name, value, valid: isValid };
    },
  }));

  return (
    <Box sx={{ width: `${width}` }}>
      <Typography sx={{ fontSize: 14 }}>
        {!_.isEmpty(label) ? `${label} ${validation?.required ? ' ' : ''}` : ''}
        {validation?.required && <span style={{ color: red[500] }}>*</span>}
      </Typography>
      <TextField
        type={type}
        onChange={event => onChange(event)}
        name={name}
        value={value}
        fullWidth
        sx={{
          width: `${width}`,

          '.MuiFormHelperText-root': {
            fontSize: '14px',
          },
        }}
        size="small"
        error={_.isEmpty(errorText) ? false : true}
        helperText={errorText}
      />
    </Box>
  );
});

export const TextFieldWithTopLabelComponent = forwardRef((props, ref) => {
  const {
    label,
    width = '100%',
    name,
    value,
    onChange,
    type = 'text',
    validation = {
      min: 1,
      max: 35,
      required: false,
    },
    customMessage = '',
  } = props;

  const [errorText, setErrorText] = useState('');

  useImperativeHandle(ref, () => ({
    onValidate() {
      let isValid = true;
      if (validation?.required) {
        if (
          value?.length >= validation?.min &&
          value?.length <= validation?.max &&
          customMessage === ''
        ) {
          setErrorText('');
          isValid = true;
        } else {
          setErrorText(
            `${label} must be between ${validation?.min} and ${validation?.max}`
          );
          isValid = false;
        }

        if (type === 'number') {
          if (value >= validation?.min && value <= validation?.max) {
            setErrorText('');
            isValid = true;
          } else {
            setErrorText(
              `${label} must be between ${validation?.min} and ${validation?.max}`
            );
            isValid = false;
          }
        }

        if (customMessage !== '') {
          if (value) {
            setErrorText('');
            isValid = true;
          } else {
            setErrorText(customMessage);
            isValid = false;
          }
        }
      } else {
        setErrorText('');
      }
      return { name, value, valid: isValid };
    },
  }));

  return (
    <Box sx={{ width: `${width}` }}>
      <Typography sx={{ fontSize: 14 }}>
        {`${label} `}
        <font color={'red'}>{validation.required && '*'}</font>{' '}
      </Typography>
      <TextField
        type={type}
        onChange={event => onChange(event)}
        name={name}
        value={value}
        fullWidth
        sx={{ width: `${width}` }}
        size="small"
        error={_.isEmpty(errorText) ? false : true}
        helperText={errorText}
      />
    </Box>
  );
});

export const TextFieldWithTopLabelComponentInvoice = forwardRef(
  (props, ref) => {
    const {
      label,
      width = '100%',
      name,
      value,
      onChange,
      type = 'text',
      validation = {
        required: false,
      },
    } = props;

    const [errorText, setErrorText] = useState('');

    useImperativeHandle(ref, () => ({
      onValidate() {
        let isValid = true;

        if (value?.length > 0) {
          setErrorText('');
          isValid = true;
        } else {
          setErrorText(`Please Enter a Number in ${label} field`);
          isValid = false;
        }
        return { name, value, valid: isValid };
      },
    }));

    const handleKeyDown = event => {
      const key = event.key;
      // Prevent input of 'e' key when type is number
      if (type.toLowerCase() === 'number') {
        if (key === 'e' || key === 'E') {
          event.preventDefault();
        }
      }
    };

    return (
      <Box sx={{ width: `${width}` }}>
        <Typography sx={{ fontSize: 14 }}>
          {!_.isEmpty(label)
            ? `${label} ${validation?.required ? ' ' : ''}`
            : ''}
          {validation?.required && <span style={{ color: red[500] }}>*</span>}
        </Typography>
        <TextField
          type={type}
          onKeyDown={handleKeyDown}
          onChange={event => onChange(event)}
          name={name}
          value={value}
          fullWidth
          sx={{
            width: `${width}`,
            '.MuiFormHelperText-root': {
              fontSize: '14px',
            },
          }}
          size="small"
          error={_.isEmpty(errorText) ? false : true}
          helperText={errorText}
        />
      </Box>
    );
  }
);

export const GenericTextFieldWithTopLabelComponent = forwardRef(
  (props, ref) => {
    const {
      label,
      width = '100%',
      name,
      value,
      onChange,
      type = 'text',
      validation = {
        min: 1,
        max: 35,
        required: false,
      },
    } = props;

    const [errorText, setErrorText] = useState('');

    useImperativeHandle(ref, () => ({
      onValidate() {
        if (
          value?.length >= validation?.min &&
          value?.length <= validation?.max
        ) {
          setErrorText('');
        } else {
          setErrorText(
            `${label} must be between ${validation?.min} and ${validation?.max}`
          );
        }
        return { name, value };
      },
    }));

    return (
      <Box sx={{ width: `${width}` }}>
        <Typography sx={{ fontSize: 14 }}>{`${label}:`} </Typography>
        <TextField
          type={type}
          onChange={event => onChange(event)}
          name={name}
          value={value}
          fullWidth
          sx={{ width: `${width}` }}
          size="small"
          error={_.isEmpty(errorText) ? false : true}
          helperText={errorText}
        />
      </Box>
    );
  }
);

export const textFieldWithTopLabel = ({
  label,
  width = '100%',
  name,
  value,
  onChange,
  disabled = false,
  type = 'text',
} = {}) => {
  return (
    <Box sx={{ width: `${width}` }}>
      <Typography sx={{ fontSize: 14 }}>{`${label}:`} </Typography>
      <TextField
        type={type}
        onChange={onChange}
        name={name}
        value={value}
        fullWidth
        sx={{ width: `${width}` }}
        size="small"
        disabled={disabled}
      />
    </Box>
  );
};

export const selectWithTopLabel = ({
  label,
  width = '100%',
  value,
  defaultValue = '',
  onChange,
  name,
  optionsList,
  disabled,
  disabledOpt,
} = {}) => {
  return (
    <>
      <FormControl sx={{ width: `${width}` }}>
        <Typography sx={{ fontSize: 14 }}>{`${label}:`} </Typography>
        <Select
          defaultValue={defaultValue}
          name={name}
          size="small"
          value={value}
          onChange={onChange}
          disabled={disabledOpt}
        >
          {optionsList?.map(item => (
            <MenuItem value={item}>{item} </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export const checkBoxWithTitleForAllergies = ({
  allergyItem,
  onClick,
  onChange,
  position = 0,
  hideCheckbox = false,
} = {}) => {
  const customEvent = {
    target: {
      name: 'allergyCheckboxGroup',
      value: allergyItem,
    },
  };
  debugger;
  let warningColor = 'gray';
  if (allergyItem?.allergyCriticality === 'low' && allergyItem?.checked) {
    warningColor = 'orange';
  }
  if (allergyItem?.allergyCriticality === 'high' && allergyItem?.checked) {
    warningColor = 'red';
  }

  let signalStrength = 'none';
  if (allergyItem?.allergySeverity === 'mild' && allergyItem?.checked) {
    signalStrength = 'low';
  }
  if (allergyItem?.allergySeverity === 'moderate' && allergyItem?.checked) {
    signalStrength = 'medium';
  }
  if (allergyItem?.allergySeverity === 'severe' && allergyItem?.checked) {
    signalStrength = 'high';
  }
  console.warn(allergyItem);

  return (
    <>
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CircleActive
            className="CircleActive"
            size={`12px`}
            color={
              allergyItem?.fromApi
                ? allergyItem?.allergyClinicalStatus === 'active'
                  ? 'lightgreen'
                  : 'red'
                : 'grey'
            }
          />
          <ErrorRoundedIcon
            style={{ fontSize: '15px', marginLeft: '5px', marginRight: '5px' }}
            sx={{ color: `${warningColor}` }}
          />
          <MobileSignalIcon
            signalStrength={signalStrength}
            width="25px"
            height="20px"
          />
          {!hideCheckbox ? (
            <Checkbox
              disabled={allergyItem?.isCheckboxDisabled}
              checked={allergyItem?.checked}
              name={allergyItem?.allergyCode}
              sx={{
                color: 'textFieldColor',
                maxWidth: '30px', // Set maximum width of the Checkbox to 30px
                flexShrink: 0,
              }}
              onChange={event => {
                onChange(
                  event,
                  allergyItem,
                  position,
                  allergyItem?.allergyCategory
                );
              }}
            />
          ) : (
            <Box px={0.5}></Box>
          )}

          {hideCheckbox ? (
            <Typography
              // onClick={() => onClick(customEvent)}
              className="ellipsis-text-view"
              sx={{
                cursor: 'pointer',
              }}
            >
              <Tooltip
                disableInteractive
                title={
                  allergyItem?.strength
                    ? allergyItem?.allergyName + ' ' + allergyItem?.strength
                    : allergyItem?.allergyName
                }
              >
                {allergyItem?.strength
                  ? allergyItem?.allergyName + ' ' + allergyItem?.strength
                  : allergyItem?.allergyName}
              </Tooltip>
            </Typography>
          ) : (
            <Typography
              onClick={() => onClick(customEvent)}
              className="ellipsis-text-edit"
              sx={{
                cursor: 'pointer',
              }}
            >
              <Tooltip
                disableInteractive
                title={
                  allergyItem?.strength
                    ? allergyItem?.allergyName + ' ' + allergyItem?.strength
                    : allergyItem?.allergyName
                }
              >
                {allergyItem?.strength ? (
                  <u>
                    {allergyItem?.allergyName} {allergyItem?.strength}
                  </u>
                ) : (
                  <u>{allergyItem?.allergyName}</u>
                )}
              </Tooltip>
            </Typography>
          )}
        </Box>
        {/* {hideCheckbox && ( */}
        {/* <> */}
        <Typography>
          <span style={{ fontWeight: 'bold' }}>Reaction:</span>{' '}
          {allergyItem?.allergyReactionCodeArray?.length > 0
            ? allergyItem.allergyReactionCodeArray.map((o, index) => (
                <>
                  {o.display}
                  {index < allergyItem.allergyReactionCodeArray.length - 1 &&
                    ', '}
                </>
              ))
            : '-'}
        </Typography>

        <Typography>
          <span style={{ fontWeight: 'bold' }}> Onset Date:</span>{' '}
          {allergyItem?.checked && allergyItem?.isCheckboxDisabled
            ? dayjs(allergyItem?.allergyOnsetDate).format('MM/DD/YYYY')
            : '-'}
        </Typography>
        {/* </>
        )} */}
      </Box>
    </>
  );
};
